import { signHmac } from '@theme/signup-consents';

const CONTACT_REQUEST_ENDPOINT = 'https://m003vybhmd.execute-api.eu-west-1.amazonaws.com/prod/form/contact-us';
const HCAPTCHA_ONLOAD = 'contactCustomerCareHcaptchaLoad';
const SHOPIFY_HCAPTCHA_WAIT_MS = 5000;

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const DEFAULT_PHONE_PREFIX = '+39';
const SHARED_FIELDS = ['name', 'email', 'phone', 'reason'];

const TIME_ZONE = 'Europe/Rome';
const FIRST_SLOT_START = 9 * 60;
const LAST_SLOT_END = 18 * 60;
const SLOT_MINUTES = 30;
const SLOT_LEAD_MINUTES = 120;

const romeClock = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** @type {Promise<any> | undefined} */
let hcaptchaReady;

/**
 * Reuses the hCaptcha script Shopify loads for its own forms, adding one only when Shopify does not.
 * @returns {Promise<any>} The global hCaptcha API.
 */
function loadHcaptcha() {
  hcaptchaReady ??= new Promise((resolve, reject) => {
    const win = /** @type {any} */ (window);

    const inject = () => {
      if (win.hcaptcha) return resolve(win.hcaptcha);

      win[HCAPTCHA_ONLOAD] = () => resolve(win.hcaptcha);
      const script = document.createElement('script');
      script.src = `https://js.hcaptcha.com/1/api.js?render=explicit&recaptchacompat=off&onload=${HCAPTCHA_ONLOAD}`;
      script.async = true;
      script.onerror = () => {
        hcaptchaReady = undefined;
        reject(new Error('hCaptcha failed to load'));
      };
      document.head.append(script);
    };

    // Shopify loads its own copy for these forms without checking for ours, so wait for it instead.
    if (win.hcaptcha || !document.querySelector('form[data-shopify-captcha]')) return inject();

    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (!win.hcaptcha && Date.now() - startedAt < SHOPIFY_HCAPTCHA_WAIT_MS) return;
      clearInterval(timer);
      inject();
    }, 100);
  });
  return hcaptchaReady;
}

/**
 * @param {string} value
 * @returns {string | null} The normalised number, or null when it is not a valid phone number.
 */
function normalizePhone(value) {
  const compact = value.replace(/[\s\-./()]/g, '');
  if (!/^\+?\d+$/.test(compact)) return null;

  const phone = compact.startsWith('+') ? compact : `${DEFAULT_PHONE_PREFIX}${compact}`;
  const digits = phone.length - 1;
  return digits >= 6 && digits <= 15 ? phone : null;
}

/**
 * @returns {{ dateKey: string, minutes: number }} Today's date (YYYY-MM-DD) and minutes since midnight in Italy.
 */
function romeNow() {
  const parts = Object.fromEntries(romeClock.formatToParts(new Date()).map(({ type, value }) => [type, value]));
  return {
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

/**
 * Noon UTC keeps the calendar date identical in Europe/Rome.
 * @param {string} dateKey
 */
function dateFromKey(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12));
}

/**
 * @param {Date} date
 */
function keyFromDate(date) {
  return date.toISOString().slice(0, 10);
}

/**
 * @param {number} minutes
 */
function formatTime(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * @param {string} time HH:MM
 */
function parseTime(time) {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * @param {number} start Minutes since midnight.
 */
function slotLabel(start) {
  return `${formatTime(start)} – ${formatTime(start + SLOT_MINUTES)}`;
}

/**
 * @param {string} phone A normalised phone number.
 */
function displayPhone(phone) {
  return phone.startsWith(DEFAULT_PHONE_PREFIX)
    ? `${DEFAULT_PHONE_PREFIX} ${phone.slice(DEFAULT_PHONE_PREFIX.length)}`
    : phone;
}

/**
 * @param {string} text
 */
function capitalize(text) {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

/**
 * @param {string} dateKey
 * @param {string | undefined} locale
 * @param {'long' | 'short'} [month]
 */
function formatDay(dateKey, locale, month = 'long') {
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    weekday: 'long',
    day: 'numeric',
    month,
    year: 'numeric',
  });
  return capitalize(formatter.format(dateFromKey(dateKey)));
}

/**
 * Start times, in minutes since midnight, of the Requested callback slots offered on a day.
 * @param {string} dateKey
 * @param {{ dateKey: string, minutes: number }} now
 */
function slotStarts(dateKey, now) {
  const weekday = dateFromKey(dateKey).getUTCDay();
  if (dateKey < now.dateKey || weekday === 0 || weekday === 6) return [];

  const starts = [];
  for (let start = FIRST_SLOT_START; start + SLOT_MINUTES <= LAST_SLOT_END; start += SLOT_MINUTES) {
    if (dateKey > now.dateKey || start >= now.minutes + SLOT_LEAD_MINUTES) starts.push(start);
  }
  return starts;
}

/**
 * @param {string} dateKey
 * @returns {string} The Europe/Rome UTC offset on that day, e.g. "+02:00".
 */
function romeOffset(dateKey) {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, timeZoneName: 'longOffset' })
    .formatToParts(dateFromKey(dateKey))
    .find((part) => part.type === 'timeZoneName')?.value;
  return !name || name === 'GMT' ? '+00:00' : name.replace('GMT', '');
}

/**
 * @param {string} dateKey
 * @param {string} startTime HH:MM in Italian time.
 */
function callbackSlot(dateKey, startTime) {
  const start = parseTime(startTime);
  const offset = romeOffset(dateKey);
  return {
    start: `${dateKey}T${formatTime(start)}:00${offset}`,
    end: `${dateKey}T${formatTime(start + SLOT_MINUTES)}:00${offset}`,
    timeZone: TIME_ZONE,
  };
}

/**
 * Select-only combobox whose value lives in a hidden input.
 */
class ContactSelect extends HTMLElement {
  #controller = new AbortController();
  /** @type {HTMLElement | null} */
  #activeOption = null;
  #placeholder = '';

  connectedCallback() {
    const { signal } = this.#controller;
    this.#placeholder = this.querySelector('[data-select-label]')?.textContent ?? '';

    this.trigger.addEventListener('click', () => this.#toggle(), { signal });
    this.trigger.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.trigger.addEventListener('blur', () => this.#close(), { signal });
    // Keeps focus on the trigger so its blur does not close the list before the click lands.
    this.list.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
    this.list.addEventListener(
      'click',
      (event) => {
        const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
        if (!(option instanceof HTMLElement)) return;
        this.#select(option);
        this.#close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get trigger() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('[role="combobox"]'));
  }

  get list() {
    return /** @type {HTMLElement} */ (this.querySelector('[role="listbox"]'));
  }

  get input() {
    return /** @type {HTMLInputElement} */ (this.querySelector('[data-select-value]'));
  }

  get value() {
    return this.input.value;
  }

  /**
   * Selects the matching option without notifying listeners, or resets when none matches.
   * @param {string} value
   */
  set value(value) {
    const option = this.#options.find((item) => item.dataset.value === value);
    if (option) this.#select(option, false);
    else this.#reset();
  }

  /**
   * @param {{ value: string, label: string }[]} options
   */
  setOptions(options) {
    this.list.replaceChildren(
      ...options.map(({ value, label }, index) => {
        const option = document.createElement('li');
        option.id = `${this.list.id}-${index + 1}`;
        option.className = 'contact-select__option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.value = value;
        option.textContent = label;
        return option;
      })
    );
    this.#reset();
    this.trigger.disabled = options.length === 0;
  }

  get #options() {
    return /** @type {HTMLElement[]} */ ([...this.list.querySelectorAll('[role="option"]')]);
  }

  #reset() {
    this.#options.forEach((item) => item.setAttribute('aria-selected', 'false'));
    this.input.value = '';
    const label = this.querySelector('[data-select-label]');
    if (label) label.textContent = this.#placeholder;
    this.removeAttribute('data-has-value');
  }

  #open() {
    if (!this.list.hidden) return;
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    const selected = this.list.querySelector('[aria-selected="true"]');
    this.#setActive(selected instanceof HTMLElement ? selected : this.#options[0]);
  }

  #close() {
    if (this.list.hidden) return;
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.#setActive(null);
  }

  #toggle() {
    if (this.list.hidden) this.#open();
    else this.#close();
  }

  /**
   * @param {HTMLElement | null | undefined} option
   */
  #setActive(option) {
    this.#activeOption?.classList.remove('is-active');
    this.#activeOption = option ?? null;

    if (!this.#activeOption) {
      this.trigger.removeAttribute('aria-activedescendant');
      return;
    }

    this.#activeOption.classList.add('is-active');
    this.trigger.setAttribute('aria-activedescendant', this.#activeOption.id);
    this.#activeOption.scrollIntoView({ block: 'nearest' });
  }

  /**
   * @param {HTMLElement} option
   * @param {boolean} [notify]
   */
  #select(option, notify = true) {
    this.#options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
    this.input.value = option.dataset.value ?? '';
    const label = this.querySelector('[data-select-label]');
    if (label) label.textContent = option.textContent?.trim() ?? '';
    this.toggleAttribute('data-has-value', this.input.value !== '');
    if (notify) this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    const isOpen = !this.list.hidden;
    const options = this.#options;
    const index = this.#activeOption ? options.indexOf(this.#activeOption) : -1;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (!isOpen) {
          this.#open();
          return;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.#setActive(options[Math.min(Math.max(index + step, 0), options.length - 1)]);
        return;
      }
      case 'Home':
      case 'End':
        if (!isOpen) return;
        event.preventDefault();
        this.#setActive(event.key === 'Home' ? options[0] : options[options.length - 1]);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          this.#open();
          return;
        }
        if (this.#activeOption) this.#select(this.#activeOption);
        this.#close();
        return;
      case 'Escape':
        if (!isOpen) return;
        event.preventDefault();
        this.#close();
        return;
    }
  }
}

/**
 * Calendar popover for the Requested callback slot day. A day is committed only through the confirm button.
 */
class ContactDatePicker extends HTMLElement {
  #controller = new AbortController();
  #placeholder = '';
  /** Month shown in the calendar, as YYYY-MM. */
  #viewMonth = '';
  #pendingKey = '';

  connectedCallback() {
    const { signal } = this.#controller;
    this.#placeholder = this.label.textContent ?? '';
    this.#renderWeekdays();

    this.trigger.addEventListener('click', () => (this.popover.hidden ? this.#open() : this.#close()), { signal });
    this.querySelector('[data-month-prev]')?.addEventListener('click', () => this.#shiftMonth(-1), { signal });
    this.querySelector('[data-month-next]')?.addEventListener('click', () => this.#shiftMonth(1), { signal });
    this.confirmButton.addEventListener('click', () => this.#confirm(), { signal });
    this.days.addEventListener('click', (event) => this.#onDayClick(event), { signal });
    this.days.addEventListener('keydown', (event) => this.#onDayKeydown(event), { signal });
    this.popover.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.#close();
        this.trigger.focus();
      },
      { signal }
    );
    document.addEventListener(
      'click',
      (event) => {
        if (!this.popover.hidden && !event.composedPath().includes(this)) this.#close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get trigger() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('[aria-haspopup="dialog"]'));
  }

  get popover() {
    return /** @type {HTMLElement} */ (this.querySelector('[role="dialog"]'));
  }

  get days() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-days]'));
  }

  get label() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-date-label]'));
  }

  get confirmButton() {
    return /** @type {HTMLButtonElement} */ (this.querySelector('[data-confirm]'));
  }

  get input() {
    return /** @type {HTMLInputElement} */ (this.querySelector('[data-date-value]'));
  }

  get value() {
    return this.input.value;
  }

  #renderWeekdays() {
    const row = this.querySelector('[data-weekdays]');
    if (!row) return;

    const narrow = new Intl.DateTimeFormat(this.dataset.locale, { weekday: 'narrow', timeZone: 'UTC' });
    const long = new Intl.DateTimeFormat(this.dataset.locale, { weekday: 'long', timeZone: 'UTC' });
    // 2024-01-01 is a Monday.
    const cells = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(Date.UTC(2024, 0, 1 + index, 12));
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.abbr = long.format(date);
      cell.textContent = narrow.format(date);
      return cell;
    });
    row.replaceChildren(...cells);
  }

  #open() {
    const now = romeNow();
    this.#pendingKey = this.value;
    this.#viewMonth = (this.value || now.dateKey).slice(0, 7);
    this.popover.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.#render();
    this.#rovingDay()?.focus();
  }

  #close() {
    if (this.popover.hidden) return;
    this.popover.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  #confirm() {
    if (!this.#pendingKey) return;

    this.input.value = this.#pendingKey;
    this.label.textContent = formatDay(this.#pendingKey, this.dataset.locale);
    this.setAttribute('data-has-value', '');
    this.#close();
    this.trigger.focus();
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {number} step
   */
  #shiftMonth(step) {
    const [year, month] = this.#viewMonth.split('-').map(Number);
    const target = keyFromDate(new Date(Date.UTC(year, month - 1 + step, 1, 12))).slice(0, 7);
    if (target < romeNow().dateKey.slice(0, 7)) return;

    this.#viewMonth = target;
    this.#render();

    const prev = this.querySelector('[data-month-prev]');
    if (prev instanceof HTMLButtonElement && prev.disabled) this.querySelector('[data-month-next]')?.focus();
  }

  #render() {
    const now = romeNow();
    const locale = this.dataset.locale;
    const [year, month] = this.#viewMonth.split('-').map(Number);
    const first = new Date(Date.UTC(year, month - 1, 1, 12));
    const daysInMonth = new Date(Date.UTC(year, month, 0, 12)).getUTCDate();
    const leading = (first.getUTCDay() + 6) % 7;
    const weeks = Math.ceil((leading + daysInMonth) / 7);

    const monthLabel = this.querySelector('[data-month-label]');
    if (monthLabel) {
      monthLabel.textContent = capitalize(
        new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(first)
      );
    }

    const prev = this.querySelector('[data-month-prev]');
    if (prev instanceof HTMLButtonElement) prev.disabled = this.#viewMonth <= now.dateKey.slice(0, 7);

    const rows = Array.from({ length: weeks }, (_, week) => {
      const row = document.createElement('tr');
      for (let weekday = 0; weekday < 7; weekday++) {
        const date = new Date(Date.UTC(year, month - 1, 1 - leading + week * 7 + weekday, 12));
        const key = keyFromDate(date);
        const cell = document.createElement('td');

        if (date.getUTCMonth() !== month - 1) {
          const day = document.createElement('span');
          day.className = 'contact-date-picker__day contact-date-picker__day--outside';
          day.textContent = String(date.getUTCDate());
          cell.setAttribute('aria-hidden', 'true');
          cell.append(day);
        } else {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'contact-date-picker__day';
          button.tabIndex = -1;
          button.dataset.date = key;
          button.textContent = String(date.getUTCDate());
          button.setAttribute('aria-label', formatDay(key, locale));
          button.disabled = slotStarts(key, now).length === 0;
          if (key === now.dateKey) button.setAttribute('aria-current', 'date');
          cell.setAttribute('aria-selected', String(key === this.#pendingKey));
          cell.append(button);
        }

        row.append(cell);
      }
      return row;
    });

    this.days.replaceChildren(...rows);
    const roving = this.#rovingDay();
    if (roving) roving.tabIndex = 0;
    this.confirmButton.disabled = !this.#pendingKey;
    this.confirmButton.setAttribute('aria-disabled', String(!this.#pendingKey));
  }

  /**
   * @returns {HTMLButtonElement | null} The day that holds keyboard focus inside the grid.
   */
  #rovingDay() {
    return (
      this.days.querySelector(`button[data-date="${this.#pendingKey}"]:not(:disabled)`) ??
      this.days.querySelector('button[data-date]:not(:disabled)')
    );
  }

  /**
   * @param {MouseEvent} event
   */
  #onDayClick(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-date]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    this.#pendingKey = button.dataset.date ?? '';
    this.#render();
    this.#rovingDay()?.focus();
  }

  /**
   * Arrow keys move between available days of the shown month.
   * @param {KeyboardEvent} event
   */
  #onDayKeydown(event) {
    const steps = /** @type {Record<string, number>} */ ({ ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 });
    const step = steps[event.key];
    const current = event.target instanceof Element ? event.target.closest('button[data-date]') : null;
    if (!step || !(current instanceof HTMLButtonElement)) return;

    event.preventDefault();
    const date = dateFromKey(current.dataset.date ?? '');
    for (;;) {
      date.setUTCDate(date.getUTCDate() + step);
      const next = this.days.querySelector(`button[data-date="${keyFromDate(date)}"]`);
      if (!(next instanceof HTMLButtonElement)) return;
      if (next.disabled) continue;

      current.tabIndex = -1;
      next.tabIndex = 0;
      next.focus();
      return;
    }
  }
}

class ContactCustomerCare extends HTMLElement {
  #controller = new AbortController();
  #inFlight = false;
  /** @type {string | undefined} */
  #captchaWidgetId;

  connectedCallback() {
    const { signal } = this.#controller;
    // Loading on first interaction gives Shopify's own hCaptcha script time to be on the page.
    this.addEventListener(
      'focusin',
      () => {
        if (this.dataset.hcaptchaSitekey) loadHcaptcha().catch(() => {});
      },
      { once: true, signal }
    );

    for (const form of this.forms) {
      form.addEventListener('input', (event) => this.#onFieldChange(form, event), { signal });
      form.addEventListener('change', (event) => this.#onFieldChange(form, event), { signal });
      form.addEventListener(
        'focusout',
        (event) => {
          if (event.target instanceof HTMLInputElement) this.#validateFieldOnBlur(event.target);
        },
        { signal }
      );
      form.addEventListener('submit', (event) => this.#onSubmit(form, event), { signal });
      this.#updateSubmitState(form);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get forms() {
    return /** @type {HTMLFormElement[]} */ ([...this.querySelectorAll('form[data-request-type]')]);
  }

  /**
   * @param {HTMLFormElement} form
   */
  #submitButton(form) {
    return /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
  }

  /**
   * @param {HTMLFormElement} form
   * @param {Event} event
   */
  #onFieldChange(form, event) {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;

    if (event.type === 'input') this.#clearFieldError(field);
    if (event.type === 'change' && field.name === 'callbackDate') this.#offerSlots(form, field.value);
    if (event.type === 'change' && (field.name === 'callbackDate' || field.name === 'callbackSlot')) {
      const slotTrigger = this.#slotTrigger(form);
      if (slotTrigger) this.#clearFieldError(slotTrigger);
    }
    if (SHARED_FIELDS.includes(field.name)) this.#shareValue(form, field.name, field.value);

    this.forms.forEach((item) => this.#updateSubmitState(item));
  }

  /**
   * Carries a shared field over to the other request types.
   * @param {HTMLFormElement} source
   * @param {string} name
   * @param {string} value
   */
  #shareValue(source, name, value) {
    for (const form of this.forms) {
      if (form === source) continue;

      const field = form.elements.namedItem(name);
      if (!(field instanceof HTMLInputElement)) continue;

      const select = field.closest('contact-select');
      if (select instanceof ContactSelect) {
        select.value = value;
      } else {
        field.value = value;
        this.#clearFieldError(field);
      }
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} dateKey
   */
  #offerSlots(form, dateKey) {
    const select = form.querySelector('contact-select[data-slot-select]');
    if (!(select instanceof ContactSelect)) return;

    const starts = dateKey ? slotStarts(dateKey, romeNow()) : [];
    select.setOptions(starts.map((start) => ({ value: formatTime(start), label: slotLabel(start) })));
  }

  /**
   * @param {HTMLFormElement} form
   */
  #slotTrigger(form) {
    const trigger = form.querySelector('contact-select[data-slot-select] [role="combobox"]');
    return trigger instanceof HTMLElement ? trigger : null;
  }

  /**
   * The chosen slot may have fallen inside the two-hour lead while the visitor was filling in the form.
   * @param {HTMLFormElement} form
   */
  #isSlotStillOffered(form) {
    const data = new FormData(form);
    const dateKey = String(data.get('callbackDate') ?? '');
    const startTime = String(data.get('callbackSlot') ?? '');
    return slotStarts(dateKey, romeNow()).includes(parseTime(startTime));
  }

  /**
   * @param {HTMLFormElement} form
   */
  #expireSlot(form) {
    this.#offerSlots(form, String(new FormData(form).get('callbackDate') ?? ''));
    const slotTrigger = this.#slotTrigger(form);
    if (slotTrigger) this.#showFieldError(slotTrigger, this.dataset.errorSlotExpired ?? '');
    this.#updateSubmitState(form);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} visible
   */
  #updateSummary(form, visible) {
    const summary = form.querySelector('[data-callback-summary]');
    if (!(summary instanceof HTMLElement)) return;

    summary.hidden = !visible;
    if (!visible) return;

    const data = new FormData(form);
    /** @param {string} name */
    const value = (name) => String(data.get(name) ?? '').trim();
    const fill = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const element = summary.querySelector(selector);
      if (element) element.textContent = text;
    };

    fill('[data-summary-reason]', value('reason'));
    fill('[data-summary-phone]', displayPhone(normalizePhone(value('phone')) ?? ''));
    fill(
      '[data-summary-when]',
      `${formatDay(value('callbackDate'), this.dataset.locale, 'short')} · ${slotLabel(parseTime(value('callbackSlot')))}`
    );
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {string} The error message, or an empty string when the field is valid or empty.
   */
  #fieldError(field) {
    const value = field.value.trim();
    if (!value) return '';
    if (field.dataset.validate === 'email' && !EMAIL_PATTERN.test(value)) return this.dataset.errorEmailInvalid ?? '';
    if (field.dataset.validate === 'phone' && !normalizePhone(value)) return this.dataset.errorPhoneInvalid ?? '';
    return '';
  }

  /**
   * @param {HTMLFormElement} form
   */
  #isValid(form) {
    const fields = /** @type {(HTMLInputElement | HTMLTextAreaElement)[]} */ ([
      ...form.querySelectorAll('input:not([type="hidden"]), textarea'),
    ]);
    const pickers = /** @type {(ContactSelect | ContactDatePicker)[]} */ ([...form.querySelectorAll('[data-required]')]);

    return (
      fields.every((field) => (!field.required || field.value.trim() !== '') && !this.#fieldError(field)) &&
      pickers.every((picker) => picker.value !== '')
    );
  }

  /**
   * @param {HTMLFormElement} form
   */
  #updateSubmitState(form) {
    const valid = this.#isValid(form);
    this.#updateSummary(form, valid);

    const button = this.#submitButton(form);
    if (!button) return;

    const disabled = this.#inFlight || !valid;
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
  }

  /**
   * @param {HTMLInputElement} field
   */
  #validateFieldOnBlur(field) {
    if (!field.dataset.validate) return;

    const message = this.#fieldError(field);
    if (message) this.#showFieldError(field, message);
    else this.#clearFieldError(field);
  }

  /**
   * @param {HTMLElement} field
   * @param {string} message
   */
  #showFieldError(field, message) {
    const wrapper = field.closest('.contact-customer-care__input-field');
    const error = document.getElementById(`${field.id}-error`);
    if (!wrapper || !error) return;

    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', error.id);
    wrapper.classList.add('contact-customer-care__input-field--error');

    const icon = this.querySelector('[data-error-icon]');
    if (icon instanceof HTMLTemplateElement && !wrapper.querySelector('.contact-customer-care__input-icon')) {
      wrapper.append(icon.content.cloneNode(true));
    }

    error.textContent = message;
    error.hidden = false;
  }

  /**
   * @param {HTMLElement} field
   */
  #clearFieldError(field) {
    const error = document.getElementById(`${field.id}-error`);
    if (!error || error.hidden) return;

    const wrapper = field.closest('.contact-customer-care__input-field');
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    wrapper?.classList.remove('contact-customer-care__input-field--error');
    wrapper?.querySelector('.contact-customer-care__input-icon')?.remove();
    error.textContent = '';
    error.hidden = true;
  }

  /**
   * @param {HTMLFormElement} form
   */
  #payload(form) {
    const data = new FormData(form);
    /** @param {string} name */
    const value = (name) => String(data.get(name) ?? '').trim();
    const type = form.dataset.requestType;

    /** @type {Record<string, unknown>} */
    const payload = {
      type,
      locale: this.dataset.locale,
      submittedAt: new Date().toISOString(),
      name: value('name'),
      email: value('email'),
      phone: normalizePhone(value('phone')),
      reason: value('reason'),
    };

    if (type === 'callback') {
      payload.callbackSlot = callbackSlot(value('callbackDate'), value('callbackSlot'));
    } else {
      payload.orderNumber = value('orderNumber');
      payload.message = value('message');
    }

    return Object.fromEntries(Object.entries(payload).filter(([, entry]) => entry));
  }

  /**
   * @returns {Promise<string>} A one-time hCaptcha response token, or an empty string without a sitekey.
   */
  async #captchaToken() {
    const sitekey = this.dataset.hcaptchaSitekey;
    if (!sitekey) return '';

    const hcaptcha = await loadHcaptcha();
    const container = this.querySelector('[data-hcaptcha]');
    this.#captchaWidgetId ??= hcaptcha.render(container, { sitekey, size: 'invisible', hl: this.dataset.locale });
    try {
      const { response } = await hcaptcha.execute(this.#captchaWidgetId, { async: true });
      return response;
    } finally {
      hcaptcha.reset(this.#captchaWidgetId);
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {SubmitEvent} event
   */
  async #onSubmit(form, event) {
    event.preventDefault();
    if (this.#inFlight || !this.#isValid(form)) return;

    if (form.dataset.requestType === 'callback' && !this.#isSlotStillOffered(form)) {
      this.#expireSlot(form);
      return;
    }

    const submitError = form.querySelector('[data-submit-error]');
    const button = this.#submitButton(form);
    if (submitError instanceof HTMLElement) submitError.hidden = true;

    this.#inFlight = true;
    button?.setAttribute('aria-busy', 'true');
    this.#updateSubmitState(form);

    try {
      // The signature covers the exact body string, so it must be sent unchanged.
      const body = JSON.stringify({ ...this.#payload(form), captcha: await this.#captchaToken() });
      const response = await fetch(CONTACT_REQUEST_ENDPOINT, {
        method: 'POST',
        headers: { Authorization: await signHmac(body), 'Content-Type': 'application/json' },
        body,
      });
      if (response.ok && (await response.json())?.success !== true) throw new Error('Contact request rejected');
      if (!response.ok) throw new Error(`Contact request failed with status ${response.status}`);
      this.#showSuccess(form.dataset.requestType);
    } catch (error) {
      const closedByVisitor = error === 'challenge-closed' || /** @type {any} */ (error)?.message === 'challenge-closed';
      if (!closedByVisitor && submitError instanceof HTMLElement) submitError.hidden = false;
    } finally {
      this.#inFlight = false;
      button?.removeAttribute('aria-busy');
      this.#updateSubmitState(form);
    }
  }

  /**
   * @param {string | undefined} type
   */
  #showSuccess(type) {
    const content = this.querySelector('[data-request-content]');
    const success = this.querySelector(`[data-request-success="${type}"]`);
    if (!(content instanceof HTMLElement) || !(success instanceof HTMLElement)) return;

    content.hidden = true;
    success.hidden = false;
    success.focus();
  }
}

if (!customElements.get('contact-select')) {
  customElements.define('contact-select', ContactSelect);
}

if (!customElements.get('contact-date-picker')) {
  customElements.define('contact-date-picker', ContactDatePicker);
}

if (!customElements.get('contact-customer-care')) {
  customElements.define('contact-customer-care', ContactCustomerCare);
}
